import { KeyedStreamItem } from 'communication/types/StreamElement';
import { StreamElementType } from 'communication/types/StreamElementType';
import { KeyedStreamElement } from 'communication/types/StreamElement';
import { StreamTimeSeriesState } from 'widgets/store/stream/WidgetStreamState';
import keyedStreamReducer from '../redux/keyedStreamReducer';
import { omitStreamMessageTypeField } from 'utils/messages/StreamMessage';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';
import { timeUtils } from '@instinet-analytics/core-lib';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';

const { mapEpochToTime, mapTimeToEpoch } = timeUtils;

/*
 * Reduces a time series array
 * Returns TRUE if the element was handled, false otherwise
 */
export default function timeSeriesReducer<
    TTimeSeriesElement extends KeyedStreamElement<StreamElementType>,
>(
    element: TTimeSeriesElement,
    nextDataState: StreamTimeSeriesState<KeyedStreamItem>,
    mapToTimeSeriesItem: (
        element: TTimeSeriesElement
    ) => KeyedStreamItem = omitStreamMessageTypeField
) {
    nextDataState.timeSeries = keyedStreamReducer(
        element,
        nextDataState.timeSeries,
        mapToTimeSeriesItem
    );
    return;
}

export function sortTimeSeries<T extends KeyedStreamItem>(
    timeSeries: T[],
    fillIntervalInSecs?: number
): T[] {
    const sorted = timeSeries.sort((a, b) => (a._i > b._i ? +1 : -1));
    if (!fillIntervalInSecs) {
        return sorted;
    }
    if (sorted.length < 2) {
        return sorted;
    }

    const indexed = [];
    for (const item of sorted) {
        indexed[item._i] = item;
    }

    const ret = [];
    for (
        let time = sorted[0]._i;
        time <= sorted[sorted.length - 1]._i;
        time = time + fillIntervalInSecs
    ) {
        ret.push(indexed[time] || ({ _i: time } as T));
    }

    return ret;
}

export function getTimeDomainEnd<T extends KeyedStreamItem>(
    data: T[],
    keys: Array<keyof T>
): number {
    if (data.length === 0) {
        return 0;
    }

    function isInDomain(item: T) {
        for (const key of keys) {
            if (item[key] !== undefined) {
                return true;
            }
        }
        return false;
    }

    const inDomain = data.filter(isInDomain);
    if (inDomain.length === 0) {
        return data[0]._i;
    }

    return Math.max(...inDomain.map((i) => i._i));
}

export function getItemTime(d: KeyedStreamItem) {
    return d._i * 60000;
}

export function getMinutes(
    startTime: number,
    endTime: number,
    breaksInTrading: TimeSeriesDisableRange[]
) {
    let ret = mapTimeToEpoch(endTime - startTime);

    for (const br of breaksInTrading) {
        const breakFromTime = mapEpochToTime(br.from);
        const breakToTime = mapEpochToTime(br.to);
        const outside =
            (breakFromTime <= startTime && breakToTime <= startTime) ||
            (breakFromTime >= endTime && breakToTime > endTime);
        if (!outside) {
            const from = Math.max(breakFromTime, startTime);
            const to = Math.min(breakToTime, endTime);
            const breakMins = mapTimeToEpoch(to - from);
            ret = ret - breakMins;
        }
    }

    return ret;
}

export function getItemTimeOrLastItem<T extends TimeSeriesChartDataItem>(
    timeSeries: T[],
    time?: number
): T | undefined {
    const lastItem = timeSeries[timeSeries.length - 1];
    if (time === undefined) {
        return lastItem;
    }

    const beforeItems = timeSeries.filter((x) => time >= x.time);
    return beforeItems.length > 0
        ? beforeItems[beforeItems.length - 1]
        : undefined;
}
